import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type SyncArticle, type ArticleSource } from '@/api';

export function SyncsList() {
  const [articles, setArticles] = useState<SyncArticle[]>([]);
  const [sources, setSources] = useState<ArticleSource[]>([]);
  const [sourceId, setSourceId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.articleSources.list().then(setSources).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    adminApi.syncArticles.list(sourceId || undefined)
      .then(setArticles)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [sourceId]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this sync article?')) return;
    try {
      await adminApi.syncArticles.delete(id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-semibold text-slate-800">Sync Articles</h1>
        <div className="flex gap-2">
          <Link
            to="/syncs/sources"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Manage sources
          </Link>
          <Link
            to="/syncs/new"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Add article
          </Link>
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Filter by source</label>
        <select
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 max-w-xs"
        >
          <option value="">All sources</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Source</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Cards</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {articles.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-3 text-slate-800 font-medium max-w-md truncate">{a.articleTitle}</td>
                <td className="px-4 py-3 text-slate-600">{a.articleSource?.name ?? '-'}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      a.published ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {a.published ? 'Live' : 'Draft'}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{a._count?.summaryCards ?? a.summaryCards?.length ?? 0}</td>
                <td className="px-4 py-3 text-slate-600">{a.publishedAt ? new Date(a.publishedAt).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/syncs/${a.id}/edit`}
                    className="text-indigo-600 hover:underline text-sm font-medium mr-4"
                  >
                    Edit
                  </Link>
                  <Link
                    to={`/syncs/${a.id}/preview`}
                    className="text-slate-600 hover:underline text-sm font-medium mr-4"
                  >
                    Preview
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(a.id)}
                    className="text-red-600 hover:underline text-sm font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {articles.length === 0 && (
        <p className="text-slate-500 mt-4">No articles yet. Add one or generate from a source URL.</p>
      )}
    </div>
  );
}
